import { toast } from "sonner";
import React, { useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import axios from "axios";
import { copyTextToClipboard } from "../../../utils/clipboard";
import StatusEditor from "../status-editor/status-editor";
import {
  ClipboardList,
  Globe,
  User,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";
export default function AdminAlbumControls({
  album = {},
  setAlbum,
  tracks = [],
}) {
  const [status, setStatus] = useState(album.status || "Draft");
  const [newOwner, setNewOwner] = useState(album.belongsTo || "");
  const [isrcValues, setIsrcValues] = useState(
    (tracks || []).reduce((acc, track) => {
      acc[track._id] = track.ISRC || "";
      return acc;
    }, {}),
  );
  const [isrcUpdating, setIsrcUpdating] = useState({});
  const [isrcModified, setIsrcModified] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isCheckingCopyright, setIsCheckingCopyright] = useState(false);

  const allTracksHaveISRC = tracks.every((track) => {
    const isrcValue = isrcValues[track._id] || track.ISRC || "";
    return isrcValue.trim() !== "";
  });

  const missingISRCTracks = tracks.filter((track) => {
    const isrcValue = isrcValues[track._id] || track.ISRC || "";
    return isrcValue.trim() === "";
  });

  const parsePDLError = (errorResponse) => {
    if (!errorResponse?.data) return "Unknown error";
    const responseData = errorResponse.data;

    if (
      responseData.additionalInfo?.errors &&
      responseData.additionalInfo.errors.length > 0
    ) {
      const albumErrors = responseData.additionalInfo.errors;
      return albumErrors
        .map((err) => {
          if (err.error && err.error.includes("PDL upload failed:")) {
            try {
              const pdlErrorStart = err.error.indexOf('{"success"');
              if (pdlErrorStart !== -1) {
                const pdlErrorJson = err.error.substring(pdlErrorStart);
                const pdlError = JSON.parse(pdlErrorJson);
                if (pdlError.data?.msg) {
                  return pdlError.data.msg
                    .replace(/\\n/g, " ")
                    .replace(/\s+/g, " ")
                    .trim();
                }
              }
            } catch (parseError) {
              console.error("Error parsing PDL error:", parseError);
            }
          }
          return err.error || "Album processing failed";
        })
        .join("; ");
    } else if (responseData.error) {
      return responseData.error;
    } else if (responseData.message) {
      return responseData.message;
    }
    return "Unknown error";
  };

  const copyExcelRows = () => {
    let excelRows = tracks.map((track, index) => {
      return [
        index + 1,
        track.title,
        "",
        index + 1,
        album.title,
        track.primaryArtist,
        track.secondaryArtist,
        "",
        track.lyricist,
        track.composer,
        album.label,
        album.productionYear,
        album.label,
        album.label,
        track.mainGenre,
        track.subGenre,
        tracks.length === 1 ? "Single" : "Album",
        track.language,
        track.language,
        track.explicit ? "Yes" : "No",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        track.ISRC,
        album.UPC,
      ];
    });
    excelRows = excelRows.map((lines) => lines.join("\t")).join("\n");
    copyTextToClipboard(excelRows).then(
      () => toast.success("Excel rows copied successfully!"),
      (err) => toast.error("Error copying excel rows."),
    );
  };

  const changeOwner = () => {
    axios
      .patch("/admin/albums/" + album._id + "/owner", { newOwner })
      .then(() => {
        toast.success("Album owner changed to " + newOwner);
        setAlbum((prev) => ({ ...prev, belongsTo: newOwner }));
      })
      .catch((e) => toast.error("Failed changing album owner"));
  };

  const updateISRC = (trackId, newISRC) => {
    if (isrcUpdating[trackId]) return;
    setIsrcUpdating((prev) => ({ ...prev, [trackId]: true }));
    const currentTrack = tracks.find((t) => t._id === trackId);
    if (!currentTrack) {
      setIsrcUpdating((prev) => ({ ...prev, [trackId]: false }));
      return;
    }

    axios
      .patch(`/admin/songs/${trackId}`, { ...currentTrack, ISRC: newISRC })
      .then(() => toast.success("ISRC updated"))
      .catch((e) => {
        toast.error("Failed to update ISRC");
        setIsrcValues((prev) => ({
          ...prev,
          [trackId]: currentTrack.ISRC || "",
        }));
        setIsrcModified((prev) => ({ ...prev, [trackId]: false }));
      })
      .finally(() =>
        setIsrcUpdating((prev) => ({ ...prev, [trackId]: false })),
      );
  };

  const handleISRCChange = (trackId, value) => {
    setIsrcValues((prev) => ({ ...prev, [trackId]: value }));
    setIsrcModified((prev) => ({ ...prev, [trackId]: true }));
  };

  const handleISRCBlur = (trackId) => {
    if (isrcModified[trackId]) {
      updateISRC(trackId, isrcValues[trackId] || "");
      setIsrcModified((prev) => ({ ...prev, [trackId]: false }));
    }
  };

  const confirmAndRelease = (serviceName, endpoint) => {
    setConfirmTitle(`Release to ${serviceName}`);
    setConfirmMessage(
      `Are you sure you want to send "${album.title}" to ${serviceName}? This action will submit the album for distribution.`,
    );
    setConfirmAction(() => () => {
      const loadingToast = toast.loading(
        `Releasing album to ${serviceName}...`,
      );
      axios
        .post(endpoint, { albumIds: [album._id] })
        .then((response) => {
          toast.dismiss(loadingToast);
          toast.success(`Album successfully sent to ${serviceName}`);
          if (response.data.data.results[0].data) {
            setAlbum((prev) => ({ ...prev, status: "SentToStores" }));
            setStatus("SentToStores");
          }
        })
        .catch((e) => {
          toast.dismiss(loadingToast);
          const errorMsg =
            parsePDLError(e.response) || e.message || "Unknown error";
          toast.error(`Failed to send to ${serviceName}: ${errorMsg}`, {
            duration: 10000,
          });
        });
    });
    setConfirmDialogOpen(true);
  };

  const runCopyrightCheck = () => {
    setIsCheckingCopyright(true);
    const checkingToast = toast.loading("Analyzing audio for copyright...");

    axios
      .post(`/admin/albums/${album._id}/copyright-check`)
      .then((res) => {
        toast.dismiss(checkingToast);
        const match = res.data?.data?.match;
        if (match) {
          toast.error(`Copyright Match: "${match.title}" by ${match.artist}`, {
            description: `Album: ${match.album} (ACRID: ${match.acrid})`,
            duration: 10000,
          });
        } else {
          toast.success("Clean: No copyright material detected", {
            duration: 5000,
          });
        }
      })
      .catch((e) => {
        toast.dismiss(checkingToast);
        const errorMsg =
          e.response?.data?.error?.message ||
          e.response?.data?.message ||
          e.message ||
          "Failed to run copyright check";
        toast.error(errorMsg);
        console.error(e);
      })
      .finally(() => setIsCheckingCopyright(false));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Release & Distribution Column */}
        <Card className="h-full">
          <CardHeader className="pb-3 border-b mb-3 bg-muted/20">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Globe className="w-4 h-4" /> Release & Distribution
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyExcelRows}
                className="h-6 text-[10px] text-muted-foreground px-2"
              >
                <ClipboardList className="w-3 h-3 mr-1" /> Meta
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Status
              </Label>
              <StatusEditor
                album={album}
                onStatusChange={(updatedAlbum, newStatus) => {
                  setStatus(newStatus);
                  setAlbum(updatedAlbum);
                }}
                triggerClassName="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Publish To
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className={`h-16 flex flex-col gap-1 items-center justify-center ${
                          allTracksHaveISRC
                            ? "border-primary/20 hover:bg-primary/5 hover:text-primary hover:border-primary/50"
                            : "opacity-70"
                        }`}
                        onClick={() =>
                          allTracksHaveISRC &&
                          confirmAndRelease("PDL", "/albums/release-to-pdl")
                        }
                        disabled={!allTracksHaveISRC}
                      >
                        <img
                          src="https://pdlindia.org/assets/img/pdl_logo_pink_new.png"
                          alt="PDL"
                          className="w-5 h-5"
                        />
                        <span className="font-semibold text-xs">
                          Publish to PDL
                        </span>
                      </Button>
                    </TooltipTrigger>
                    {!allTracksHaveISRC && (
                      <TooltipContent>
                        <p>Missing ISRCs</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className={`h-16 flex flex-col gap-1 items-center justify-center ${
                          allTracksHaveISRC
                            ? "border-primary/20 hover:bg-primary/5 hover:text-primary hover:border-primary/50"
                            : "opacity-70"
                        }`}
                        onClick={() =>
                          allTracksHaveISRC &&
                          confirmAndRelease("Broma", "/albums/release-to-broma")
                        }
                        disabled={!allTracksHaveISRC}
                      >
                        <img
                          src="/favicon.png"
                          alt="RayApp"
                          className="w-5 h-5"
                        />
                        <span className="font-semibold text-xs">
                          Publish to RayApp
                        </span>
                      </Button>
                    </TooltipTrigger>
                    {!allTracksHaveISRC && (
                      <TooltipContent>
                        <p>Missing ISRCs</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-16 flex flex-col gap-1 items-center justify-center border-primary/20 hover:bg-primary/5 hover:text-primary hover:border-primary/50"
                        onClick={runCopyrightCheck}
                        disabled={isCheckingCopyright}
                      >
                        <ShieldAlert
                          className={`w-5 h-5 ${isCheckingCopyright ? "animate-pulse" : ""}`}
                        />
                        <span className="font-semibold text-xs">
                          {isCheckingCopyright
                            ? "Checking..."
                            : "Check Copyright"}
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Run manual ACRCloud scan</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ownership Column */}
        <Card className="h-full">
          <CardHeader className="pb-3 border-b mb-3 bg-muted/20">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <User className="w-4 h-4" /> Ownership
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-3 border flex items-center justify-between">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center border shadow-sm">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                    Current Owner
                  </p>
                  <p className="text-sm font-bold truncate">
                    {album.belongsTo || "Unknown"}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Transfer To
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newOwner}
                  onChange={(e) => setNewOwner(e.target.value)}
                  placeholder="Recipient User ID"
                  className="h-9 text-xs font-mono"
                />
                <Button size="sm" onClick={changeOwner} className="px-4">
                  Transfer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ISRC Assignment */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-3 border-b mb-0 bg-muted/20">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> ISRC Management
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {tracks && tracks.length > 0 ? (
              <div className="divide-y">
                {tracks.map((track, index) => (
                  <div
                    key={track._id}
                    className="p-3 flex items-center gap-4 hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-8 text-center text-sm text-muted-foreground font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {track.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {track.primaryArtist}
                      </div>
                    </div>
                    <div className="w-48">
                      <Input
                        className="h-8 font-mono text-xs"
                        placeholder="ISRC Code"
                        value={isrcValues[track._id] || ""}
                        onChange={(e) =>
                          handleISRCChange(track._id, e.target.value)
                        }
                        onBlur={() => handleISRCBlur(track._id)}
                        disabled={isrcUpdating[track._id]}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground text-sm">
                No tracks available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmTitle}</DialogTitle>
            <DialogDescription>{confirmMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmAction) confirmAction();
                setConfirmDialogOpen(false);
              }}
            >
              Yes, Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
